import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import partnerApi, { errMsg } from '../api';

const LOCKABLE = ['price', 'offer_price', 'title', 'description'];

function LockToggle({ field, locked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(field, !locked)}
      title={locked ? 'Field is locked — crawler won\'t overwrite' : 'Click to lock this field'}
      className={`flex items-center gap-1 rounded px-2 py-0.5 text-xs transition-colors ${
        locked
          ? 'bg-raspberry-50 text-raspberry-700 border border-raspberry-200'
          : 'border border-hairline text-ink-muted hover:border-raspberry-200 hover:text-raspberry-600'
      }`}
    >
      {locked ? '🔒 Locked' : '🔓 Unlocked'}
    </button>
  );
}

export default function ProductDetail() {
  const { mira_sku } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [analytics, setAnalytics] = useState(null);

  // Edit state — raw fields only (title, not display_title)
  const [edits, setEdits] = useState({ price: '', offer_price: '', title: '', description: '' });
  const [locked, setLocked] = useState({ price: false, offer_price: false, title: false, description: false });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await partnerApi.get(`/products/${mira_sku}`);
      setProduct(data);
      const override = data._override || {};
      const overrideValues = override.overrides || {};
      const lockedFields = override.locked_fields || [];
      setEdits({
        price: String(overrideValues.price ?? data.price ?? ''),
        offer_price: String(overrideValues.offer_price ?? data.offer_price ?? ''),
        title: overrideValues.title ?? data.title ?? '',
        description: overrideValues.description ?? data.description ?? '',
      });
      setLocked({
        price: lockedFields.includes('price'),
        offer_price: lockedFields.includes('offer_price'),
        title: lockedFields.includes('title'),
        description: lockedFields.includes('description'),
      });
    } catch (err) {
      toast.error(errMsg(err, 'Could not load product'));
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [mira_sku]); // eslint-disable-line

  useEffect(() => {
    partnerApi.get(`/analytics/products/${mira_sku}`)
      .then(({ data }) => setAnalytics(data))
      .catch(() => setAnalytics(null));
  }, [mira_sku]);

  const handleEdit = (field) => (e) => {
    setEdits((prev) => ({ ...prev, [field]: e.target.value }));
    setDirty(true);
  };

  const handleLock = (field, value) => {
    setLocked((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      const overrides = {};
      const locked_fields = [];
      for (const field of LOCKABLE) {
        const val = edits[field];
        if (val !== '' && val != null) {
          overrides[field] = field === 'price' || field === 'offer_price' ? Number(val) : val;
        }
        if (locked[field]) locked_fields.push(field);
      }
      await partnerApi.patch(`/products/${mira_sku}`, { overrides, locked_fields });
      toast.success('Changes saved');
      setDirty(false);
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not save changes'));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    if (!product) return;
    const isInactive = product._override?.status === 'inactive';
    const action = isInactive ? 'activate' : 'deactivate';
    setBusy(true);
    try {
      await partnerApi.post(`/products/${mira_sku}/${action}`);
      toast.success(isInactive ? 'Product restored to feed' : 'Product removed from feed');
      load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not update product'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-sm text-ink-muted">Loading…</div>
    );
  }
  if (!product) return null;

  const isInactive = product._override?.status === 'inactive';
  const imageUrl = product.image_urls?.[0];

  return (
    <div>
      {/* Breadcrumb */}
      <div className="mb-6 flex items-center gap-2 text-sm text-ink-muted">
        <Link to="/products" className="hover:text-ink">Products</Link>
        <span>/</span>
        <span className="text-ink">{product.title || product.mira_sku}</span>
      </div>

      <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
        {/* Left — image + status */}
        <div className="space-y-4">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={product.title}
              className="w-full rounded-xl border border-hairline object-cover"
              style={{ maxHeight: 320 }}
            />
          ) : (
            <div className="flex h-60 w-full items-center justify-center rounded-xl border border-hairline bg-surface-page">
              <span className="text-sm text-ink-muted">No image</span>
            </div>
          )}

          {/* Deactivate / Activate */}
          <button
            onClick={toggleActive}
            disabled={busy}
            className={`w-full rounded-lg border py-2.5 text-sm font-medium transition-colors disabled:opacity-50 ${
              isInactive
                ? 'border-raspberry-200 bg-raspberry-50 text-raspberry-700 hover:bg-raspberry-100'
                : 'border-hairline text-ink-secondary hover:border-status-critical hover:text-status-critical'
            }`}
          >
            {isInactive ? '↑ Restore to feed' : '↓ Remove from feed'}
          </button>

          {isInactive && (
            <div className="rounded-lg border border-status-serious bg-orange-50 px-3 py-2 text-xs text-orange-700">
              This product is hidden from mahi.fashion shoppers.
            </div>
          )}

          {/* Product metadata (read-only) */}
          <div className="rounded-xl border border-hairline bg-surface p-4 text-xs space-y-2">
            <MetaRow label="SKU" value={product.mira_sku} mono />
            <MetaRow label="Brand" value={product.brand} />
            <MetaRow label="Category" value={product.category} />
            <MetaRow label="Source" value={product.source_website} />
          </div>
        </div>

        {/* Right — edit form */}
        <div>
          <div className="rounded-xl border border-hairline bg-surface p-6">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-base font-semibold text-ink">Edit product details</h2>
              {dirty && (
                <span className="text-xs text-status-warning">Unsaved changes</span>
              )}
            </div>

            <div className="space-y-6">
              <EditField
                label="Title"
                hint="Displayed to shoppers"
                value={edits.title}
                onChange={handleEdit('title')}
                locked={locked.title}
                onLockChange={handleLock}
                field="title"
                placeholder={product.title}
              />
              <EditField
                label="Price (₹)"
                value={edits.price}
                onChange={handleEdit('price')}
                locked={locked.price}
                onLockChange={handleLock}
                field="price"
                type="number"
                placeholder={String(product.price ?? '')}
              />
              <EditField
                label="Offer price (₹)"
                hint="Leave empty if no sale price"
                value={edits.offer_price}
                onChange={handleEdit('offer_price')}
                locked={locked.offer_price}
                onLockChange={handleLock}
                field="offer_price"
                type="number"
                placeholder={String(product.offer_price ?? '')}
              />
              <EditField
                label="Description"
                value={edits.description}
                onChange={handleEdit('description')}
                locked={locked.description}
                onLockChange={handleLock}
                field="description"
                multiline
                placeholder={product.description}
              />
            </div>

            <div className="mt-6 flex items-center justify-between border-t border-hairline pt-5">
              <p className="text-xs text-ink-muted">
                Locked fields are preserved across crawl syncs.
              </p>
              <button
                onClick={save}
                disabled={busy || !dirty}
                className="rounded-lg bg-raspberry-600 px-5 py-2 text-sm font-medium text-white hover:bg-raspberry-700 disabled:opacity-40"
              >
                {busy ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>

          {/* 30-day click chart */}
          <ClickChart analytics={analytics} />
        </div>
      </div>
    </div>
  );
}

function EditField({ label, hint, value, onChange, locked, onLockChange, field, type = 'text', multiline, placeholder }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <label className="text-sm font-medium text-ink-secondary">
          {label}
          {hint && <span className="ml-1 text-xs font-normal text-ink-muted">· {hint}</span>}
        </label>
        <LockToggle field={field} locked={locked} onChange={onLockChange} />
      </div>
      {multiline ? (
        <textarea
          rows={4}
          value={value}
          onChange={onChange}
          placeholder={placeholder || ''}
          className="w-full rounded-lg border border-hairline px-3 py-2 text-sm text-ink placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-raspberry-300"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder || ''}
          className="w-full rounded-lg border border-hairline px-3 py-2 text-sm text-ink placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-raspberry-300"
        />
      )}
    </div>
  );
}

export function ClickChart({ analytics }) {
  if (!analytics) return null;
  const { daily, total_clicks } = analytics;
  if (!daily || daily.length === 0) return null;

  const max = Math.max(...daily.map((d) => d.clicks), 1);

  return (
    <div className="mt-6 rounded-xl border border-hairline bg-surface p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
          Clicks — last 30 days
        </h3>
        <span className="text-xs font-medium text-ink">{total_clicks} total</span>
      </div>

      {total_clicks === 0 ? (
        <p className="py-6 text-center text-xs text-ink-muted">
          No click data yet — clicks appear within 24 hours of going live.
        </p>
      ) : (
        <div className="flex h-24 items-end gap-px">
          {daily.map((d) => (
            <div
              key={d.date}
              className="group relative flex-1"
              style={{ height: '100%', display: 'flex', alignItems: 'flex-end' }}
            >
              <div
                className="w-full rounded-t bg-raspberry-200 group-hover:bg-raspberry-500 transition-colors"
                style={{ height: `${Math.max(2, (d.clicks / max) * 100)}%` }}
              />
              {/* Tooltip on hover */}
              {d.clicks > 0 && (
                <div className="absolute -top-7 left-1/2 -translate-x-1/2 hidden group-hover:block whitespace-nowrap rounded bg-ink px-1.5 py-0.5 text-[10px] text-white">
                  {d.date}: {d.clicks}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* X-axis: first and last date */}
      {daily.length > 0 && (
        <div className="mt-1 flex justify-between text-[10px] text-ink-muted">
          <span>{daily[0]?.date}</span>
          <span>{daily[daily.length - 1]?.date}</span>
        </div>
      )}
    </div>
  );
}

function MetaRow({ label, value, mono }) {
  return (
    <div className="flex gap-2">
      <span className="w-16 flex-shrink-0 text-ink-muted">{label}</span>
      <span className={`flex-1 truncate text-ink ${mono ? 'font-mono' : ''}`}>{value || '—'}</span>
    </div>
  );
}
